"use client"

import Link from "next/link"
import { ArrowRight, BookOpen, CheckCircle2 } from "lucide-react"

import { analytics, ctaSurfaceProps, trackEvent } from "@/lib/analytics"
import { useImpressionEvent } from "@/hooks/useImpressionEvent"
import { cn } from "@/lib/utils"

type ProductGuideCtaVariant = "inline" | "result"

interface ProductGuideCtaProps {
  source: string
  variant?: ProductGuideCtaVariant
  className?: string
}

const GUIDE_HREF = "/lawn-products-guide"

const COPY: Record<ProductGuideCtaVariant, {
  eyebrow: string
  title: string
  description: string
  bullets: string[]
  cta: string
}> = {
  inline: {
    eyebrow: "Free buyer's guide",
    title: "Before you buy the bag, bottle, or sprayer",
    description:
      "Get the 2026 lawn products guide: the picks worth buying, the combo products to skip, and the timing mistakes that waste money.",
    bullets: ["35 picks", "12 skips", "No sponsored rankings"],
    cta: "Get the guide",
  },
  result: {
    eyebrow: "Next step",
    title: "Match this result to products that are actually worth buying",
    description:
      "The 2026 buyer's guide covers fertilizer, weed control, seed, sprayers, soil tests, watering gear, and the products I would leave on the shelf.",
    bullets: ["Fertilizer", "Weed control", "Tools"],
    cta: "Get the free guide",
  },
}

// The inline variant (guides, blog, weed and pest pages) keeps the visual shell
// from the Claude Design "Buyers Guide CTA" prototype (glass card,
// glass icon tile, green heading, glassy proof pills, full-width gradient CTA),
// token-mapped onto ds-glass / font-editorial / Tailwind utilities. The result
// variant sits in tool result stacks and uses the tool card and button recipe.
export function ProductGuideCta({
  source,
  variant = "inline",
  className,
}: ProductGuideCtaProps) {
  const copy = COPY[variant]
  // CTR denominator. The /lawn-products-guide gate converts at ~54%, far and
  // away the best capture surface on the site, but it only saw 24 impressions
  // in the 14 days to 2026-07-22 — it is starved, not weak. Without an
  // impression here we could not tell whether this CTA is rarely SHOWN or
  // rarely CLICKED, which is exactly the question that decides what to fix.
  const ctaLocation = `product_guide_${source}`
  // One bag on both sides: the click used to carry only cta_location, so a
  // click could not be attributed back to the impression's surface or variant.
  const ctaProps = ctaSurfaceProps(ctaLocation, {
    surface: 'product_guide_cta',
    destination: GUIDE_HREF,
    variant,
  })
  const impressionRef = useImpressionEvent<HTMLElement>(
    ctaLocation,
    ctaProps,
    { emit: analytics.ctaSurfaceShown },
  )

  if (variant === "result") {
    return (
      <aside
        ref={impressionRef}
        aria-labelledby={`product-guide-cta-${source}`}
        className={cn(
          "my-8 rounded-2xl border border-gray-200 bg-white p-5 shadow-elevation-2 md:p-7",
          className,
        )}
      >
        <div className="md:flex md:items-center md:gap-8">
          <div className="min-w-0 flex-1">
            <h2
              id={`product-guide-cta-${source}`}
              className="text-fluid-h2 font-bold tracking-tight text-gray-900"
            >
              {copy.title}
            </h2>
            <p className="mt-2 max-w-2xl text-sm leading-relaxed text-gray-600 md:text-[15px]">
              {copy.description}
            </p>
          </div>
          <Link
            href={GUIDE_HREF}
            onClick={() =>
              trackEvent("cta_clicked", {
                ...ctaProps,
                cta_text: copy.cta,
              })
            }
            className="press mt-5 flex min-h-11 w-full items-center justify-center gap-2 rounded-xl bg-action px-6 text-base font-bold text-action-foreground no-underline hover:bg-action-hover md:mt-0 md:w-64 md:shrink-0"
          >
            {copy.cta}
            <ArrowRight className="h-5 w-5" aria-hidden="true" />
          </Link>
        </div>
      </aside>
    )
  }

  return (
    <aside
      ref={impressionRef}
      aria-labelledby={`product-guide-cta-${source}`}
      className={cn(
        "ds-glass my-8 rounded-[20px] p-5 transition-[transform,box-shadow] duration-300 hover:-translate-y-1 md:p-7",
        className,
      )}
    >
      {/* Stacked card on mobile (prototype proportions); content + CTA columns
          on desktop so wide listing/tool slots don't leave dead space right. */}
      <div className="md:flex md:items-center md:gap-8">
        <div className="min-w-0 flex-1">
          {/* Eyebrow row: glass icon tile + label */}
          <div className="flex items-center gap-3">
            <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl border border-white/80 bg-linear-to-b from-white/90 to-green-50/75 text-green-600 shadow-[0_6px_16px_rgba(22,163,74,0.14),inset_0_1px_0_rgba(255,255,255,0.9)]">
              <BookOpen className="h-[22px] w-[22px]" aria-hidden="true" />
            </span>
            <span className="text-xs font-bold uppercase tracking-wider text-green-700">
              {copy.eyebrow}
            </span>
          </div>

          <h3
            id={`product-guide-cta-${source}`}
            className="font-editorial mb-2 mt-4 text-xl font-bold leading-snug tracking-tight text-green-800 md:text-[22px]"
          >
            {copy.title}
          </h3>
          <p className="max-w-2xl text-sm leading-relaxed text-gray-600 md:text-[15px]">
            {copy.description}
          </p>

          {/* Proof pills */}
          <div className="mt-4 flex flex-wrap gap-2">
            {copy.bullets.map((bullet) => (
              <span
                key={bullet}
                className="inline-flex items-center gap-1.5 rounded-full border border-green-200/80 bg-green-100/65 px-3 py-1.5 text-[13px] font-semibold text-green-800 backdrop-blur-xs"
              >
                <CheckCircle2 className="h-3.5 w-3.5 text-green-600" aria-hidden="true" />
                {bullet}
              </span>
            ))}
          </div>
        </div>

        {/* Gradient CTA: full-width below content on mobile, right column on desktop */}
        <Link
          href={GUIDE_HREF}
          onClick={() =>
            trackEvent("cta_clicked", {
              ...ctaProps,
              cta_text: copy.cta,
            })
          }
          className="mt-5 flex min-h-14 w-full items-center justify-center gap-2.5 rounded-full border border-white/35 bg-linear-to-r from-green-600/95 to-emerald-600/95 px-6 text-base font-bold tracking-tight text-white shadow-[0_10px_24px_-6px_rgba(22,163,74,0.45),inset_0_1px_0_rgba(255,255,255,0.35)] transition hover:-translate-y-0.5 hover:shadow-[0_16px_32px_-6px_rgba(22,163,74,0.55),inset_0_1px_0_rgba(255,255,255,0.4)] focus:outline-2 focus:outline-offset-2 focus:outline-green-700 md:mt-0 md:w-64 md:shrink-0 lg:w-72"
        >
          {copy.cta}
          <ArrowRight className="h-5 w-5" aria-hidden="true" />
        </Link>
      </div>
    </aside>
  )
}
